import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Stethoscope, Check, CreditCard, ArrowLeft, Shield, Clock, RefreshCw, AlertCircle, GraduationCap, Users, BarChart3, Star } from 'lucide-react';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Payment Method Update Form Component
const PaymentMethodForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError('');

    try {
      // Get setup intent from backend
      const token = localStorage.getItem('token');
      const { data } = await axios.post('/subscription/update-payment-method', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Confirm the setup
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      // Confirm with backend
      await axios.post('/subscription/confirm-payment-update', {
        setup_intent_id: data.setup_intent_id,
        payment_method_id: setupIntent.payment_method
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Payment method updated successfully!');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update payment method');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border rounded-lg bg-white">
        <CardElement options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': { color: '#aab7c4' }
            },
            invalid: { color: '#9e2146' }
          }
        }} />
      </div>
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      <div className="flex gap-3">
        <Button type="submit" disabled={loading || !stripe} className="flex-1 bg-[#0F766E] hover:bg-[#0d6960]">
          {loading ? 'Updating...' : 'Update Payment Method'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

const SubscriptionPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpdatePayment, setShowUpdatePayment] = useState(false);
  const [convertingTrial, setConvertingTrial] = useState(false);
  const isTrialSetup = searchParams.get('setup') === 'trial';

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/subscription/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(data);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post('/payments/create-checkout', {
        origin_url: originUrl,
        mode: isTrialSetup ? 'setup' : 'subscription'
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
      setLoading(false);
    }
  };

  const handleConvertTrial = async () => {
    setConvertingTrial(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post('/subscription/convert-trial', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(data.message);
      fetchSubscriptionStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate subscription');
    } finally {
      setConvertingTrial(false);
    }
  };

  const handleEducatorSubscribe = async () => {
    setSubscribingEducator(true);
    try {
      const token = localStorage.getItem('token');
      
      // Check if user has payment method
      if (!hasPaymentMethod) {
        toast.error('Please add a payment method first');
        setShowUpdatePayment(true);
        setSubscribingEducator(false);
        return;
      }
      
      const { data } = await axios.post('/subscription/educator', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(data.message);
      fetchSubscriptionStatus();
      
      // Redirect to educator dashboard
      setTimeout(() => navigate('/educator'), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate educator subscription');
    } finally {
      setSubscribingEducator(false);
    }
  };

  const features = [
    'Unlimited AI-powered patient scenarios',
    'Complete EHR documentation practice',
    'Evidence-based feedback & evaluation',
    'Performance tracking & analytics',
    'NANDA-I care planning tools',
    'Multiple clinical environments',
    'NCLEX-aligned assessments',
    'Monthly scenario updates'
  ];

  const educatorFeatures = [
    'All Pro features included',
    'Invite unlimited students to your class',
    'Track individual student progress',
    'Class performance analytics',
    'NCLEX score breakdowns by category',
    'Student activity monitoring',
    'Export class reports',
    'Priority support'
  ];

  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [subscribingEducator, setSubscribingEducator] = useState(false);

  const isActive = subscriptionStatus?.subscription_status === 'active';
  const isTrialing = subscriptionStatus?.subscription_status === 'trialing';
  const hasPaymentMethod = subscriptionStatus?.payment_method_saved;
  const trialExpired = subscriptionStatus?.trial_expired;
  const canConvert = subscriptionStatus?.can_convert;
  const willAutoCharge = subscriptionStatus?.will_auto_charge;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100 dark:from-slate-900 dark:to-slate-950">
      <nav className="border-b border-border bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button data-testid="back-button" variant="outline" onClick={() => navigate('/dashboard')} className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {isTrialSetup ? 'Skip for Now' : 'Dashboard'}
            </Button>
            <div className="flex items-center gap-3">
              <Stethoscope className="w-8 h-8 text-primary" />
              <span className="text-xl font-bold text-primary font-['Manrope']">ClinicalSim AI</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-16" data-testid="subscription-page">
        <div className="max-w-4xl mx-auto">
          {/* Current Subscription Status */}
          {subscriptionStatus && (
            <div className={`mb-8 p-6 rounded-xl border-2 ${
              isActive ? 'bg-green-50 border-green-500' : 
              isTrialing ? 'bg-blue-50 border-blue-500' :
              trialExpired ? 'bg-red-50 border-red-400' : 
              'bg-blue-50 border-blue-400'
            }`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold mb-2">
                    {isActive ? '✓ Active Subscription' : 
                     isTrialing ? `🎉 Trial Active - ${subscriptionStatus.trial_days_remaining} days remaining` :
                     trialExpired ? '⚠ Trial Expired' : 
                     `Free Trial - ${subscriptionStatus.trial_days_remaining} days remaining`}
                  </h3>
                  {hasPaymentMethod && subscriptionStatus.payment_method && (
                    <p className="text-sm text-slate-600">
                      Payment Method: {subscriptionStatus.payment_method.brand.toUpperCase()} •••• {subscriptionStatus.payment_method.last4}
                      <span className="ml-2">Exp: {subscriptionStatus.payment_method.exp_month}/{subscriptionStatus.payment_method.exp_year}</span>
                    </p>
                  )}
                  {/* Auto-charge notice for trialing users */}
                  {isTrialing && willAutoCharge && (
                    <div className="mt-3 p-3 bg-blue-100 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <Shield className="w-4 h-4 inline mr-1" />
                        <strong>Auto-renewal enabled:</strong> Your card will be automatically charged $4.99/month when your trial ends
                        {subscriptionStatus.trial_ends_at && (
                          <span> on {new Date(subscriptionStatus.trial_ends_at).toLocaleDateString()}</span>
                        )}.
                      </p>
                    </div>
                  )}
                  {!hasPaymentMethod && (
                    <p className="text-sm text-orange-600">No payment method on file</p>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowUpdatePayment(true)}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  {hasPaymentMethod ? 'Update Card' : 'Add Card'}
                </Button>
              </div>

              {/* Trial Expired - Can Convert */}
              {canConvert && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> By clicking "Activate Subscription", you authorize us to charge your saved payment method 
                      <strong> $4.99/month</strong> automatically each month until you cancel.
                    </p>
                  </div>
                  <Button 
                    onClick={handleConvertTrial}
                    disabled={convertingTrial}
                    className="bg-[#0F766E] hover:bg-[#0d6960]"
                  >
                    {convertingTrial ? 'Processing...' : 'Activate Subscription ($4.99/month)'}
                  </Button>
                </div>
              )}

              {/* Trial Expired - No Payment Method */}
              {trialExpired && !hasPaymentMethod && !isActive && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700 font-medium mb-2">
                      Your 7-day free trial has expired
                    </p>
                    <p className="text-sm text-red-600 mb-3">
                      To continue using ClinicalSim AI, please add a payment method below. 
                      You will be charged <strong>$4.99/month</strong> automatically until you cancel.
                    </p>
                    <Button 
                      onClick={() => setShowUpdatePayment(true)}
                      className="bg-[#0F766E] hover:bg-[#0d6960] text-white"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Add Payment Method to Continue
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Payment Method Update Modal */}
          {showUpdatePayment && (
            <div className="mb-8 p-6 rounded-xl bg-white border-2 border-slate-200 shadow-lg">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                {hasPaymentMethod ? 'Update Payment Method' : 'Add Payment Method'}
              </h3>
              <Elements stripe={stripePromise}>
                <PaymentMethodForm 
                  onSuccess={() => {
                    setShowUpdatePayment(false);
                    fetchSubscriptionStatus();
                  }}
                  onCancel={() => setShowUpdatePayment(false)}
                />
              </Elements>
            </div>
          )}

          {/* Trial Setup Header */}
          {isTrialSetup && !subscriptionStatus?.payment_method_saved && (
            <div className="mb-8 p-6 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-[#0F766E]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#0F766E] flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold font-['Manrope'] mb-2" style={{color: '#0f172a'}}>
                    Complete Your Free Trial Setup
                  </h2>
                  <p className="text-sm" style={{color: '#475569'}}>
                    Add a payment method to activate your <strong>7-day free trial</strong>. 
                    You won't be charged today. After your trial ends, you'll be automatically 
                    subscribed at <strong>$4.99/month</strong>. Cancel anytime.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold font-['Manrope'] mb-4">
              {isActive ? 'Your Subscription' : 
               isTrialSetup ? 'Start Your 7-Day Free Trial' : 
               'Choose Your Plan'}
            </h1>
            <p className="text-lg text-muted-foreground">
              {isActive ? 'Thank you for being a subscriber!' : 
               subscriptionStatus?.subscription_tier === 'educator' ? 'You have the Educator plan' :
               'Get full access to all features and start mastering your clinical skills today'}
            </p>
          </div>

          {/* Two-Plan Pricing Layout */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Pro Plan */}
            <div className={`medical-card p-8 shadow-xl relative ${selectedPlan === 'pro' ? 'ring-2 ring-[#0F766E]' : ''}`}>
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-4">
                  <Stethoscope className="w-4 h-4" />
                  For Nursing Students
                </div>
                <h3 className="text-2xl font-bold font-['Manrope'] mb-2">Pro</h3>
                <div className="text-5xl font-bold text-primary mb-2 font-['Manrope']">
                  $4.99
                  <span className="text-xl text-muted-foreground">/month</span>
                </div>
                <p className="text-sm text-muted-foreground">7-day free trial • Cancel anytime</p>
              </div>

              <div className="space-y-3 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-success/10 flex items-center justify-center mt-0.5">
                      <Check className="w-3 h-3 text-success" />
                    </div>
                    <span className="text-sm text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {!isActive && !showUpdatePayment && (
                <Button 
                  data-testid="subscribe-pro-button"
                  size="lg" 
                  onClick={handleSubscribe} 
                  disabled={loading || subscriptionStatus?.subscription_tier === 'educator'}
                  className="w-full bg-[#0F766E] hover:bg-[#0d6960] text-white h-12"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {loading ? 'Processing...' : (isTrialSetup ? 'Start Free Trial' : 'Subscribe to Pro')}
                </Button>
              )}

              {(isActive && subscriptionStatus?.subscription_tier !== 'educator') && (
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Check className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <p className="text-green-700 font-medium text-sm">Active Subscription</p>
                </div>
              )}
            </div>

            {/* Educator Plan */}
            <div className={`medical-card p-8 shadow-xl relative border-2 border-amber-300 ${selectedPlan === 'educator' ? 'ring-2 ring-amber-500' : ''}`}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1 bg-amber-500 text-white text-sm font-semibold rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  For Educators
                </span>
              </div>
              
              <div className="text-center mb-6 pt-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
                  <GraduationCap className="w-4 h-4" />
                  Clinical Instructors
                </div>
                <h3 className="text-2xl font-bold font-['Manrope'] mb-2">Educator</h3>
                <div className="text-5xl font-bold text-amber-600 mb-2 font-['Manrope']">
                  $24.99
                  <span className="text-xl text-muted-foreground">/month</span>
                </div>
                <p className="text-sm text-muted-foreground">No trial • Billed immediately</p>
              </div>

              <div className="space-y-3 mb-8">
                {educatorFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center mt-0.5">
                      <Check className="w-3 h-3 text-amber-600" />
                    </div>
                    <span className="text-sm text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {subscriptionStatus?.subscription_tier === 'educator' && subscriptionStatus?.educator_subscription_active ? (
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <GraduationCap className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-amber-700 font-medium text-sm">Active Educator Subscription</p>
                </div>
              ) : (
                <Button 
                  data-testid="subscribe-educator-button"
                  size="lg" 
                  onClick={handleEducatorSubscribe} 
                  disabled={subscribingEducator || !hasPaymentMethod}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white h-12"
                >
                  {subscribingEducator ? (
                    'Processing...'
                  ) : !hasPaymentMethod ? (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Add Payment Method First
                    </>
                  ) : (
                    <>
                      <GraduationCap className="w-4 h-4 mr-2" />
                      Upgrade to Educator
                    </>
                  )}
                </Button>
              )}
              
              {!hasPaymentMethod && (
                <p className="text-xs text-center text-amber-600 mt-2">
                  Add a payment method above to subscribe
                </p>
              )}
            </div>
          </div>

          {/* Educator Plan Benefits Callout */}
          {!subscriptionStatus?.educator_subscription_active && (
            <div className="mt-8 p-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border-2 border-amber-200 max-w-3xl mx-auto">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-amber-800 mb-2">Why Educator Plan?</h3>
                  <p className="text-amber-700 text-sm">
                    Perfect for nursing instructors, clinical educators, and preceptors. Invite your students, 
                    monitor their NCLEX preparation progress, and identify areas needing additional support. 
                    When your subscription ends, student invitations automatically expire.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Trust Badges */}
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">Trusted by nursing students and professionals</p>
            <div className="flex justify-center gap-8 flex-wrap">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                <span className="text-sm">NCLEX Aligned</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-success" />
                <span className="text-sm">Evidence-Based</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-accent" />
                <span className="text-sm">Secure Payment</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Award = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="7" />
    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
  </svg>
);

export default SubscriptionPage;
